import type { ComponentDefinition } from "./types";

/**
 * Plausibility checks on a runtime-authored `ComponentDefinition`'s declared
 * amps. Dependency-free (one type-only import) so the AI endpoint can gate
 * model output with it. Reports only — never repairs a value, because a
 * re-derived "plausible" number would sit behind an ABYC-cited calculation.
 */

export type ImplausibleAmps =
  | {
      reason: "surge-below-draw";
      ampDraw: number;
      ampSurge: number;
    }
  | {
      reason: "engine-surge-dwarfs-draw";
      ampDraw: number;
      ampSurge: number;
      /** `ampSurge / max(ampDraw, 1)`, unrounded. */
      ratio: number;
    };

/**
 * Above this, an `engine`'s surge/draw pair is treated as transposed rather
 * than merely unusual: locked-rotor inrush is conventionally 5–8× full-load
 * current and 10× at the extreme, and the catalog's engines span 1.4×–2.2×.
 */
export const ENGINE_SURGE_RATIO_LIMIT = 20;

/**
 * The contradiction in `def`'s declared amps, or `null` — which includes "not
 * enough declared to judge". An absent *or zero* `ampSurge` is never a finding.
 */
export function checkImplausibleAmps(
  def: Pick<ComponentDefinition, "category" | "ampDraw" | "ampSurge">,
): ImplausibleAmps | null {
  const { ampDraw, ampSurge } = def;
  if (ampDraw == null || ampSurge == null) return null;
  if (!Number.isFinite(ampDraw) || !Number.isFinite(ampSurge)) return null;
  if (ampSurge <= 0) return null;

  // Peak resolves to `max(continuous, surge)`, so a lower surge is silently
  // ignored rather than applied.
  if (ampSurge < ampDraw) return { reason: "surge-below-draw", ampDraw, ampSurge };

  if (def.category === "engine") {
    const ratio = ampSurge / Math.max(ampDraw, 1);
    if (ratio >= ENGINE_SURGE_RATIO_LIMIT) {
      return { reason: "engine-surge-dwarfs-draw", ampDraw, ampSurge, ratio };
    }
  }

  return null;
}
